import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, FormControl,
  InputLabel, Select, MenuItem, Grid, Button, Autocomplete, InputAdornment,
  FormControlLabel, Switch, Alert
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { statusOptions, positionOptions } from '../../../utils/Enums';
import { getStaff } from '../../../services/UserService';

const FRANCHISE_STAFF_POSITION_IDS = [2, 4, 7];

const AddUserModal = ({ open, onClose, onSave, roles = [], locations = [], businessUnits = [], isBusinessUnitScoped = false }) => {

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [staff, setStaff] = useState([]);
  const [companyDomain, setCompanyDomain] = useState('');

  const getInitialFormData = () => ({
    emailName: '', // Solo la parte del nombre del email
    ci: '',
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    roleId: '',
    dateOfAdmission: dayjs(),
    status: '',
    address: '',
    birthdate: dayjs(),
    position: '',
    canAccessWebDashboard: false,
    manager: null,
    scopeType: 'ASSIGNED_LOCATIONS',
    businessUnitId: '',
    locationIds: []
  });

  const [formData, setFormData] = useState(getInitialFormData);
  const [errors, setErrors] = useState({});
  const availablePositionOptions = isBusinessUnitScoped
    ? positionOptions.filter((position) => FRANCHISE_STAFF_POSITION_IDS.includes(position.id))
    : positionOptions;

  const dependencyWarnings = React.useMemo(() => {
    if (!open) return [];

    const warnings = [];

    if (!roles.length) {
      warnings.push('Antes de crear un usuario de staff es necesario crear un rol.');
    }

    if (formData.scopeType === 'BUSINESS_UNIT' && !businessUnits.length) {
      warnings.push('Antes de crear un usuario con alcance de franquicia es necesario crear una franquicia.');
    }

    return warnings;
  }, [businessUnits.length, formData.scopeType, open, roles.length]);

  const hasBlockingDependencies = dependencyWarnings.length > 0;

  const shouldForceWebDashboardAccess = (roleId) => {
    const selectedRole = roles.find((role) => role.id === roleId);
    return ['ECOSYSTEM_MANAGER', 'FRANCHISE_MANAGER'].includes(selectedRole?.templateCode);
  };

  const getRecommendedScopeType = (roleId) => {
    const selectedRole = roles.find((role) => role.id === roleId);
    switch (selectedRole?.templateCode) {
      case 'ECOSYSTEM_ADMIN':
      case 'ECOSYSTEM_MANAGER':
        return 'ALL';
      case 'MAIN_MANAGER':
        return 'MAIN';
      case 'FRANCHISE_MANAGER':
        return 'BUSINESS_UNIT';
      case 'SALES':
      case 'PRODUCTION':
      case 'SALES_PRODUCTION':
        return 'ASSIGNED_LOCATIONS';
      default:
        return null;
    }
  };

  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData());
      setErrors({});
      getStaff().then(setStaff);
      
      // Obtener el dominio del email desde localStorage
      const storedDomain = localStorage.getItem("emailDomain");
      if (storedDomain) {
        setCompanyDomain(storedDomain);
      } else {
        // Fallback si no hay dominio guardado
        setCompanyDomain('@miempresa.com');
      }
    }
  }, [open]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validate = () => {
    const tempErrors = {};
    let isValid = true;

    // Construir el email completo
    const fullEmail = formData.emailName.trim() + companyDomain;

    if (!formData.emailName.trim()) {
      tempErrors.emailName = t('email_required');
      isValid = false;
    } else if (!validateEmail(fullEmail)) {
      tempErrors.emailName = t('email_invalid');
      isValid = false;
    }

    if (!formData.ci.trim()) {
      tempErrors.ci = t('ci_required');
      isValid = false;
    }

    if (!formData.firstName.trim()) {
      tempErrors.firstName = t('firstName_required');
      isValid = false;
    }

    if (!formData.lastName.trim()) {
      tempErrors.lastName = t('lastName_required');
      isValid = false;
    }

    if (!formData.phone.trim()) {
      tempErrors.phone = t('phone_required');
      isValid = false;
    }

    if (!formData.password.trim()) {
      tempErrors.password = t('password_required');
      isValid = false;
    }

    if (!formData.roleId) {
      tempErrors.roleId = t('role_required');
      isValid = false;
    }

    if (!formData.dateOfAdmission) {
      tempErrors.dateOfAdmission = t('dateOfAdmission_required');
      isValid = false;
    }

    if (!formData.status && formData.status !== 0) {
      tempErrors.status = t('status_required');
      isValid = false;
    }

    if (!formData.address.trim()) {
      tempErrors.address = t('address_required');
      isValid = false;
    }

    if (!formData.birthdate) {
      tempErrors.birthdate = t('birthdate_required');
      isValid = false;
    }

    if (!formData.position && formData.position !== 0) {
      tempErrors.position = t('position_required');
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (hasBlockingDependencies) return;
    if (!validate()) return;

    setLoading(true);
    try {
      // Construir el email completo antes de enviar
      const fullEmail = formData.emailName.trim() + companyDomain;
      const dataToSave = {
        ...formData,
        email: fullEmail,
        scopes: buildScopes(formData)
      };
      
      await onSave(dataToSave);
      onClose();
    } catch (error) {
      if (error.response?.status === 409) {
        setErrors((prev) => ({ ...prev, emailName: t('email_already_registered') }));
      } else {
        console.error('Error inesperado al guardar:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'roleId') {
      const recommendedScopeType = getRecommendedScopeType(value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        canAccessWebDashboard: shouldForceWebDashboardAccess(value)
          ? true
          : prev.canAccessWebDashboard,
        ...(recommendedScopeType
          ? {
              scopeType: recommendedScopeType,
              businessUnitId: recommendedScopeType === 'BUSINESS_UNIT' ? prev.businessUnitId : '',
            }
          : {}),
      }));
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleWebDashboardAccessChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      canAccessWebDashboard: event.target.checked
    }));
  };

  const handleDateChange = (name, newValue) => {
    setFormData({ ...formData, [name]: newValue });
  };

  const handleManagerChange = (_, value) => {
    setFormData({ ...formData, manager: value });
  };

  const buildScopes = (data) => {
    switch (data.scopeType) {
      case 'ALL':
        return [{ scope_type: 'ALL' }];
      case 'MAIN':
        return [{ scope_type: 'MAIN' }];
      case 'BUSINESS_UNIT':
        return data.businessUnitId ? [{ scope_type: 'BUSINESS_UNIT', business_unit_id: Number(data.businessUnitId) }] : [];
      case 'ASSIGNED_LOCATIONS':
      default:
        return [{ scope_type: 'ASSIGNED_LOCATIONS' }];
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle><span className="roboto-regular">{t('add_user')}</span></DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {dependencyWarnings.map((warning) => (
            <Grid item xs={12} key={warning}>
              <Alert severity="error">{warning}</Alert>
            </Grid>
          ))}
          <Grid item xs={12} sm={6} style={{marginTop: "4px"}}>
            <TextField
              label={t('first_name')}
              fullWidth
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
          </Grid>
          <Grid item xs={12} sm={6} style={{marginTop: "4px"}}>
            <TextField
              label={t('last_name')}
              fullWidth
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('email')}
              fullWidth
              name="emailName"
              value={formData.emailName}
              onChange={handleChange}
              error={!!errors.emailName}
              helperText={errors.emailName}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ color: '#666', fontSize: '14px' }}>
                      {companyDomain}
                    </span>
                  </InputAdornment>
                ),
              }}
              placeholder="nombre"
            />
          </Grid>
          <Grid item xs={12} sm={6} style={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.canAccessWebDashboard}
                  onChange={handleWebDashboardAccessChange}
                />
              }
              label="Acceso al dashboard web"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('ci')}
              fullWidth
              name="ci"
              value={formData.ci}
              onChange={handleChange}
              error={!!errors.ci}
              helperText={errors.ci}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('phone')}
              fullWidth
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.roleId}>
              <InputLabel>{t('role')}</InputLabel>
              <Select
                label={t('role')}
                name="roleId"
                value={formData.roleId}
                onChange={handleChange}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={staff}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              isOptionEqualToValue={(option, value) => option.email === value?.email}
              value={formData.manager}
              onChange={handleManagerChange}
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${t('manager')} (${t('optional')})`}
                  placeholder={t('select_manager')}
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Alcance</InputLabel>
              <Select
                label="Alcance"
                name="scopeType"
                value={formData.scopeType}
                onChange={handleChange}
              >
                <MenuItem value="ALL">Ecosistema</MenuItem>
                <MenuItem value="MAIN">Main</MenuItem>
                <MenuItem value="BUSINESS_UNIT">Franquicia</MenuItem>
                <MenuItem value="ASSIGNED_LOCATIONS">Sucursales asignadas</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {formData.scopeType === 'BUSINESS_UNIT' && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Franquicia</InputLabel>
                <Select
                  label="Franquicia"
                  name="businessUnitId"
                  value={formData.businessUnitId}
                  onChange={handleChange}
                >
                  {businessUnits.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id}>{unit.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              label={t('password')}
              type="password"
              fullWidth
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label={t('date_of_admission')}
              value={formData.dateOfAdmission}
              onChange={(newValue) => handleDateChange('dateOfAdmission', newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.dateOfAdmission}
                  helperText={errors.dateOfAdmission}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.status}>
              <InputLabel>{t('status')}</InputLabel>
              <Select
                label={t('status')}
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status.id} value={status.id}>{t(status.label)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('address')}
              fullWidth
              name="address"
              value={formData.address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label={t('birthdate')}
              value={formData.birthdate}
              onChange={(newValue) => handleDateChange('birthdate', newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.birthdate}
                  helperText={errors.birthdate}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.position}>
              <InputLabel>{t('position')}</InputLabel>
              <Select
                label={t('position')}
                name="position"
                value={formData.position}
                onChange={handleChange}
              >
                {availablePositionOptions.map((position) => (
                  <MenuItem key={position.id} value={position.id}>{t(position.label)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <LoadingButton loading={loading} disabled={hasBlockingDependencies} onClick={handleSave} variant="contained" color="primary">
          {t('confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserModal;
